import dayjs from 'dayjs';

import i18n from '@/core/i18n';
import { DATE_KEY_FORMAT } from '@/helpers';

import { NO_DEPARTAMENT_ID } from '../constants';
import { AssignmentType, RowItem, RowItemType, TimeOffBlockType } from '../types';
import { PlannerDepartmentResponse } from '../types/dto';
import { mapAssignmentsFromPlannerResponse } from './assignment.mapper';
import { mapTimeOffsFromPlannerResponse } from './time-off.mapper';

/**
 * Adapter to convert PlannerDepartmentResponse[] from API to the format
 * expected by getPlannerDataByDepartments utility function.
 *
 * Transforms API response structure to match the planner UI requirements.
 *
 * @param loadedDateRange - The full date range that has been fetched. Dates within
 *   this range that have no group-level utilization data are marked as noDataDates
 *   so the UI can show "-" instead of a misleading 0%.
 */
export const adaptPlannerDepartmentsToDepartmentData = (
  departments: PlannerDepartmentResponse[] | undefined,
  loadedDateRange?: { startDate: string; endDate: string } | null
): {
  rows: RowItem[];
  assignments: Map<string, AssignmentType[]>;
  timeOffs: Map<string, TimeOffBlockType[]>;
} => {
  if (!departments || departments.length === 0) {
    return { rows: [], assignments: new Map(), timeOffs: new Map() };
  }

  const result = departments.reduce(
    (acc, departmentResponse) => {
      const departmentId = departmentResponse.department?.id
        ? String(departmentResponse.department.id)
        : NO_DEPARTAMENT_ID;
      const departmentName =
        departmentResponse.department?.name || i18n.t('authenticated.planner:noDepartment');

      // Convert utilization array to map for department (extract date part as key)
      // Fix percent when backend returns 0% for groups with 0 capacity but assigned work
      const departmentUtilizationMap = new Map(
        departmentResponse.utilizationByDate.map(util => [
          dayjs(util.date).format(DATE_KEY_FORMAT),
          util.capacity === 0 ? { ...util, percent: 100 } : util,
        ])
      );

      // Detect dates where group-level utilization is unavailable. Two cases:
      // 1. Cross-group backfill: a member was copied from another group — the member
      //    has data but the group-level aggregation doesn't.
      // 2. Empty horizontal fetch: the backend returned no items for a date range
      //    (e.g. items: []) — dates in that range have no utilization for either
      //    the group or its members. We detect these by iterating the loaded date
      //    range and flagging any weekday not present in group-level utilization.
      const groupUtilDates = new Set(
        departmentResponse.utilizationByDate.map(u => dayjs(u.date).format(DATE_KEY_FORMAT))
      );
      const noDataDates = new Set<string>();

      // Case 1: member has utilization for a date the group doesn't
      departmentResponse.members.forEach(member => {
        member.utilizationByDate.forEach(u => {
          const dateKey = dayjs(u.date).format(DATE_KEY_FORMAT);
          if (!groupUtilDates.has(dateKey)) {
            noDataDates.add(dateKey);
          }
        });
      });

      // Case 2: loaded date range has weekdays with no group utilization at all
      if (loadedDateRange) {
        let current = dayjs(loadedDateRange.startDate);
        const end = dayjs(loadedDateRange.endDate);
        while (!current.isAfter(end)) {
          const isWeekend = current.day() === 0 || current.day() === 6;
          if (!isWeekend) {
            const dateKey = current.format(DATE_KEY_FORMAT);
            if (!groupUtilDates.has(dateKey)) {
              noDataDates.add(dateKey);
            }
          }
          current = current.add(1, 'day');
        }
      }

      // Add department group row
      acc.rows.push({
        id: departmentId,
        title: departmentName,
        hoursPerDay: 8,
        type: RowItemType.Group,
        utilizationByDate: departmentUtilizationMap,
        ...(noDataDates.size > 0 && { noDataDates }),
      });

      // Process each member in the department
      departmentResponse.members.forEach(member => {
        const userId = String(member.user.id);
        const currentState = member.stateChanges[0];
        const memberHoursPerDay = currentState?.workingMinutesPerDay
          ? currentState.workingMinutesPerDay / 60
          : 8;

        // Convert utilization array to map for member (extract date part as key)
        // Fix percent when backend returns 0% for members with 0 capacity but assigned work
        const memberUtilizationMap = new Map(
          member.utilizationByDate.map(util => [
            dayjs(util.date).format(DATE_KEY_FORMAT),
            util.capacity === 0 ? { ...util, percent: 100 } : util,
          ])
        );

        // The same user can appear under multiple departments (e.g. "Frontend" and
        // "No department") when they changed departments during the loaded date range.
        // Use departmentId+userId as the key so each row gets its own assignment set
        // with the correct belongsToTheGroup values instead of being overwritten by
        // the last department processed.
        const assignmentKey = `${departmentId}:${userId}`;

        // Add user row under the department
        acc.rows.push({
          id: assignmentKey,
          title: member.user.name,
          hoursPerDay: memberHoursPerDay,
          user: member.user,
          type: RowItemType.User,
          stateChanges: member.stateChanges,
          utilizationByDate: memberUtilizationMap,
          parentGroupId: departmentId,
        });

        // Map assignments for this user — belongsToTheGroup is taken directly from
        // the backend response which correctly reflects department membership periods.
        const mappedAssignments = mapAssignmentsFromPlannerResponse(member.assignments);
        acc.assignments.set(assignmentKey, mappedAssignments);

        // Map time-offs for this user
        const mappedTimeOffs = mapTimeOffsFromPlannerResponse(member.timeOffs);
        acc.timeOffs.set(assignmentKey, mappedTimeOffs);
      });

      return acc;
    },
    { rows: [], assignments: new Map(), timeOffs: new Map() } as {
      rows: RowItem[];
      assignments: Map<string, AssignmentType[]>;
      timeOffs: Map<string, TimeOffBlockType[]>;
    }
  );

  // Sort user rows alphabetically WITHIN their parent group.
  const groupIndices: number[] = [];
  result.rows.forEach((row, i) => {
    if (row.type === RowItemType.Group) groupIndices.push(i);
  });

  const sortedRows: RowItem[] = [];
  groupIndices.forEach((groupIdx, gi) => {
    sortedRows.push(result.rows[groupIdx]);
    const nextGroupIdx = gi + 1 < groupIndices.length ? groupIndices[gi + 1] : result.rows.length;
    const members = result.rows.slice(groupIdx + 1, nextGroupIdx);
    members.sort((a, b) =>
      a.title.trim().toLocaleLowerCase().localeCompare(b.title.trim().toLocaleLowerCase())
    );
    sortedRows.push(...members);
  });
  result.rows = sortedRows;

  return result;
};
